import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { isQuestion } from '@atlassian/search-ai/ai-answer-question-detection/question-detection';
import { QueryIntent } from '@atlassian/search-ai/constants';
import { hasSAINLayerParamValue } from '@atlassian/search-ai/get-sain-layer-frontend-experiment';
import { getQueryIntent } from '@atlassian/search-ai/getQueryIntent';
import { localStore as knowledgeDiscoveryLocalStore } from '@atlassian/search-ai/local-store';
import { getSmartAnswerRoute } from '@atlassian/search-ai/query-intent-api';
import { type IntentClassificationData } from '@atlassian/search-ai/query-intent-api/types';

import {
	onQueryIntentFailure,
	onQueryIntentFired,
} from '../../common/utils/events/smart-answers-interacted';
import { useAnalytics, useCommonAndPrivateAttributes } from '../analytics';
import { useBootstrap } from '../store/bootstrap';
import { useSearchQuery } from '../store/search';

export const useQueryIntent: () => {
	loading: boolean;
	transformedQuery: string;
	queryIntent: QueryIntent;
	queryIntentClassificationData: IntentClassificationData | undefined;
	requestId: string | undefined;
	traceId: string | undefined;
} = () => {
	const intl = useIntl();
	const [searchQuery] = useSearchQuery();
	const [loading, setLoading] = useState(false);
	const [queryIntent, setQueryIntent] = useState(QueryIntent.DEFAULT);
	const [transformedQuery, setTransformedQuery] = useState('');
	const [queryIntentClassificationData, setQueryIntentClassificationData] = useState<
		IntentClassificationData | undefined
	>();
	const [requestId, setRequestId] = useState<string | undefined>();
	const [traceId, setTraceId] = useState<string | undefined>();
	const [{ analyticsAttributes, cloudId, primaryProduct, orgId }] = useBootstrap();
	const siteARI = `ari:cloud:${primaryProduct}::site/${cloudId}`;
	// plex code uses "jira" as the primary product key but we need to use "jira-software" instead for intent detection
	const jiraSiteARI = `ari:cloud:jira-software::site/${cloudId}`;
	const workspaceId = analyticsAttributes?.activationId
		? `ari:cloud:${primaryProduct}:${cloudId}:workspace/${analyticsAttributes.activationId}`
		: '';
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const numberOfRecentDocuments = useMemo(() => {
		if (!fg('kd_recent_activity_intent') || !searchQuery) {
			return undefined;
		}
		return Number(knowledgeDiscoveryLocalStore.getString('recent-activity-count'));
	}, [searchQuery]);
	const metadata = !!numberOfRecentDocuments ? { numberOfRecentDocuments } : undefined;

	const isIntentDetectionV2OnHelloEnabled = fg('kd_intent_v2_exp_gate');
	const isIntentDetectionV2CustomerTestCohort =
		hasSAINLayerParamValue('intent_v2_cohort', 'test') && fg('kd_intent_v2_customer_exp_gate');

	const onQueryIntentFailed = useCallback(() => {
		fireAnalyticsEvent(
			onQueryIntentFailure({
				commonAttributes,
				nonPrivacySafeAttributes,
				cloudId,
			}),
		);

		if (isQuestion({ query: searchQuery, locale: intl.locale })) {
			setQueryIntent(QueryIntent.NATURAL_LANGUAGE_QUERY);
		}
	}, [
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		intl.locale,
		searchQuery,
		cloudId,
	]);

	const onQueryIntentFire = useCallback(() => {
		fireAnalyticsEvent(
			onQueryIntentFired({
				commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
	}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes]);

	useEffect(() => {
		if (!searchQuery) {
			return;
		}

		const callGetSmartAnswerRoute = async () => {
			// this key targets only jira_web so it should be false for other products
			const actualSiteId = primaryProduct === 'jira' ? jiraSiteARI : siteARI;
			setLoading(true);
			onQueryIntentFire();
			// getQueryIntent and getSmartAnswerRoute internally catches and
			// sets queryIntent,transformedQuery to defaults when errored
			// no need for a try catch here
			if (isIntentDetectionV2OnHelloEnabled || isIntentDetectionV2CustomerTestCohort) {
				await getQueryIntent({
					query: searchQuery,
					locale: intl.locale,
					siteId: actualSiteId,
					orgId: orgId ?? '',
					workspaceId,
					cloudId: cloudId ?? '',
					product: primaryProduct || '',
					setQueryIntent,
					setTransformedQuery,
					setIntentData: setQueryIntentClassificationData,
					onError: onQueryIntentFailed,
					setRequestId,
					setTraceId,
					metadata,
				});
			} else {
				await getSmartAnswerRoute({
					query: searchQuery,
					locale: intl.locale,
					siteId: actualSiteId,
					orgId: orgId ?? '',
					workspaceId,
					cloudId: cloudId ?? '',
					product: primaryProduct || '',
					setQueryIntent,
					setTransformedQuery,
					onError: onQueryIntentFailed,
					metadata,
				});
			}
			setLoading(false);
		};

		callGetSmartAnswerRoute();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteARI, intl.locale, searchQuery]);

	return {
		loading,
		transformedQuery,
		queryIntent,
		queryIntentClassificationData,
		requestId,
		traceId,
	};
};
